import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faCheck, faBox, faTag, faFolder, faTrash,
    faTshirt, faPalette, faRuler, faRulerHorizontal, faInfoCircle, faDollarSign,
    faWarehouse, faEdit, faPlus, faSync, faImages, faScissors, faCog
} from '@fortawesome/free-solid-svg-icons';
import baseURL from '../../url';
import './EditProduct.css';
import { useColors } from '../../../hooks/useColors';
import { useTallas } from '../../../hooks/useTallas';
import ColorManager from '../ColorManager/ColorManager';
import TallaManager from '../TallaManager/TallaManager';

// ===== CONSTANTES =====
// Ahora las tallas se cargan dinámicamente desde la base de datos

// Los colores ahora se cargan dinámicamente desde la base de datos
// const COLORES_DISPONIBLES = []; // Removido - ahora se usa useColors hook

// Componente Toast
const Toast = ({ toasts }) => {
    if (toasts.length === 0) return null;
    return (
        <div className="product-toast-container">
            {toasts.map(t => (
                <div key={t.id} className={`product-toast ${t.type}`}>
                    <div className="toast-icon">
                        {t.type === 'success' && '✅'}
                        {t.type === 'error' && '❌'}
                        {t.type === 'warning' && '⚠️'}
                        {t.type === 'info' && 'ℹ️'}
                    </div>
                    <div className="toast-content">
                        <div className="toast-title">{t.message}</div>
                    </div>
                </div>
            ))}
        </div>
    );
};

// Hook para manejar toasts
const useToast = () => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 4000);
    }, []);

    return { toasts, showToast };
};

// Hook para manejar las variaciones
const useVariaciones = (productId, showToast) => {
    const [variaciones, setVariaciones] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const cargarVariaciones = useCallback(async () => {
        if (!productId) return;
        setIsLoading(true);
        try {
            const r = await fetch(`${baseURL}/productoDetalleGet.php?idProducto=${productId}`);
            if (!r.ok) {
                const errorData = await r.json();
                console.error('Backend error', errorData);
                showToast(errorData?.error || 'No se pudo cargar el producto', 'error');
                setVariaciones([]);
                return;
            }
            const data = await r.json();

            const grupos = (data.colores || []).map(c => {
                const imgs = Array.isArray(c.imagenes) ? c.imagenes : [];
                return {
                    idColorBase: Number(c.idProductoColor),
                    color: c.color || '',
                    colorHex: c.colorHex || '#000000',
                    tallas: Array.isArray(c.tallas)
                        ? c.tallas.map(t => ({
                            idItem: Number(t.idProductoTalla),
                            talla: t.talla,
                            stock: Number(t.stock || 0)
                        }))
                        : [],
                    imagenesExistentes: imgs.map(img => ({
                        idImagenVariacion: Number(img.idImagen),
                        imagen: img.imagen,
                        alt: img.alt || '',
                        titulo: img.titulo || '',
                        esPrincipal: Number(img.es_principal) === 1,
                        isExisting: true
                    })),
                    imagenesNuevas: [],
                    imagenesAEliminar: [],
                };
            });
            setVariaciones(grupos);
        } catch (e) {
            console.error(e);
            showToast('Error cargando variaciones', 'error');
            setVariaciones([]);
        } finally {
            setIsLoading(false);
        }
    }, [productId, showToast]);

    useEffect(() => { cargarVariaciones(); }, [cargarVariaciones]);

    return { variaciones, setVariaciones, isLoading, recargarVariaciones: cargarVariaciones };
};

// ===== COMPONENTE PRINCIPAL =====
export default function EditProduct({ product, onClose, onSaved, categorias, showToast, moneda }) {
    // Hook de colores dinámicos
    const { coloresActivos, cargarColores } = useColors();
    // Hook de tallas dinámicas
    const { tallasActivas, tallasComoStrings, tiposTallas, cargarTallas, cargarTiposTallas, obtenerTallasPorTipo } = useTallas();

    const { toasts, showToast: internalShowToast } = useToast();
    const { variaciones, setVariaciones, recargarVariaciones, isLoading } = useVariaciones(product?.idProducto, internalShowToast);

    const finalShowToast = showToast || internalShowToast;

    // ESTADOS
    const [formData, setFormData] = useState({
        nombreProducto: '',
        categoriaSeleccionada: '',
        precioProducto: '',
        precioAnteriorProducto: '',
        costoProducto: '',
        descripcionActual: '',
        tipoTallaSeleccionado: ''
        // Nota: tipoTallaSeleccionado se mantiene por compatibilidad pero ya no se usa
    });

    const [tallasPersonalizadas, setTallasPersonalizadas] = useState([]);
    const [nuevaTallaPersonalizada, setNuevaTallaPersonalizada] = useState('');
    const [tipoTallaSeleccionado, setTipoTallaSeleccionado] = useState('');
    const [tallasDisponibles, setTallasDisponibles] = useState([]);

    const [variacionForm, setVariacionForm] = useState({
        idVariacionAEditar: null,
        colorActual: '',
        tallasSeleccionadas: [],
        stockPorTalla: {},
        imagenesDraft: []
    });

    const [mostrarFormularioVariacion, setMostrarFormularioVariacion] = useState(false);
    const [mensaje, setMensaje] = useState('');
    const [bootDone, setBootDone] = useState(false);
    const [showColorManager, setShowColorManager] = useState(false);
    const [showTallaManager, setShowTallaManager] = useState(false);

    // FUNCIÓN AUXILIAR PARA NORMALIZAR TEXTO
    const norm = useCallback((s) => (s || '').toString().trim().toLowerCase(), []);

    // Función para abrir el administrador de tallas
    const abrirTallaManager = () => {
        setShowTallaManager(true);
    };

    // MEMOIZED VALUES
    const usedColors = useMemo(
        () => new Set(variaciones.map(v => norm(v.color))),
        [variaciones, norm]
    );

    // Cargar tallas basadas en el tipo seleccionado
    useEffect(() => {
        const cargarTallasPorTipo = async () => {
            if (tipoTallaSeleccionado) {
                const tallasDelTipo = await obtenerTallasPorTipo(tipoTallaSeleccionado);
                setTallasDisponibles(tallasDelTipo.map(t => t.nombre));
            } else {
                // NO mostrar todas las tallas por defecto - dejar vacío
                setTallasDisponibles([]);
            }
        };

        cargarTallasPorTipo();
    }, [tipoTallaSeleccionado]);

    // Preseleccionar el tipo de talla basado en el código guardado en el producto
    useEffect(() => {
        if (bootDone && product?.tipoTalla && !tipoTallaSeleccionado && tiposTallas.length > 0) {
            // Buscar el tipo de talla por su código guardado
            const tipoTallaObj = tiposTallas.find(t => t.codigo === product.tipoTalla);

            if (tipoTallaObj) {
                setTipoTallaSeleccionado(tipoTallaObj.idTipoTalla);
            }
        }
    }, [bootDone, product?.tipoTalla, tipoTallaSeleccionado, tiposTallas]);

    const resumen = useMemo(() => {
        const stockTotal = variaciones.reduce((acc, v) => {
            const subtotal = (v.tallas || []).reduce((s, t) => s + Number(t.stock || 0), 0);
            return acc + subtotal;
        }, 0);

        const margen = (formData.precioProducto && formData.costoProducto)
            ? (((Number(formData.precioProducto) - Number(formData.costoProducto)) / Number(formData.precioProducto)) * 100).toFixed(1)
            : 0;

        return { stockTotal, totalVariaciones: variaciones.length, margen };
    }, [variaciones, formData.precioProducto, formData.costoProducto]);

    // EFFECTS
    useEffect(() => {
        if (!product?.idProducto) return;
        const initialData = {
            nombreProducto: product.titulo || '',
            precioProducto: product.precio ?? '',
            precioAnteriorProducto: product.precioAnterior ?? '',
            costoProducto: product.costo ?? '',
            descripcionActual: product.descripcion || '',
            tipoTallaSeleccionado: product.tipoTalla || '',
        };
        const cat = product.idCategoria ? String(product.idCategoria) : '';
        const tallas = product.tallasPersonalizadas ? JSON.parse(product.tallasPersonalizadas) : [];
        setTallasPersonalizadas(tallas);

        setFormData(prev => ({ ...prev, ...initialData, categoriaSeleccionada: cat }));
        setBootDone(true);
    }, [product]);

    // LÓGICA DEL FORMULARIO DE VARIACIONES (ADD/EDIT)
    const resetVariacionForm = useCallback(() => {
        setVariacionForm({
            idVariacionAEditar: null,
            colorActual: '',
            tallasSeleccionadas: [],
            stockPorTalla: {},
            imagenesDraft: []
        });
    }, []);

    const iniciarEdicionVariacion = useCallback((variacion) => {
        const stockPorTalla = {};
        (variacion.tallas || []).forEach(x => {
            stockPorTalla[x.talla] = String(x.stock ?? 0);
        });

        // Unimos las imágenes existentes y nuevas en un solo draft
        const allImages = [
            ...(variacion.imagenesExistentes || []).map(img => ({ ...img, isExisting: true })),
            ...(variacion.imagenesNuevas || []).map(img => ({ ...img, isExisting: false, isNew: true }))
        ];

        setVariacionForm({
            idVariacionAEditar: variacion.idColorBase,
            colorActual: variacion.color,
            tallasSeleccionadas: (variacion.tallas || []).map(x => x.talla),
            stockPorTalla,
            imagenesDraft: allImages
        });
        setMostrarFormularioVariacion(true);
        finalShowToast(`Editando color: ${variacion.color}`, 'info');
    }, [finalShowToast]);

    // Función para manejar la carga de archivos en el formulario de variación
    const handleFileChangeInForm = useCallback((e) => {
        const arr = Array.from(e.target.files || []);
        // Usamos un identificador único para cada nueva imagen
        const next = arr.map(file => ({ id: Date.now() + Math.random(), file, preview: URL.createObjectURL(file), isNew: true }));

        setVariacionForm(prev => {
            const merged = [...prev.imagenesDraft, ...next];
            const hasPrincipal = merged.some(img => img.esPrincipal);
            if (!hasPrincipal && merged.length > 0) {
                merged[0].esPrincipal = true;
            }
            return { ...prev, imagenesDraft: merged };
        });
        e.target.value = ''; // Limpiar el input file
    }, []);

    // Función para marcar una imagen como principal en el formulario de variación
    const setPrincipalImagenInForm = useCallback((imgId, isExisting) => {
        setVariacionForm(prev => {
            const updatedImages = prev.imagenesDraft.map(img => {
                const isCurrent = isExisting ? img.idImagenVariacion === imgId : img.file === imgId;
                return {
                    ...img,
                    esPrincipal: isCurrent
                };
            });
            return { ...prev, imagenesDraft: updatedImages };
        });
    }, []);

    // Función para eliminar una imagen en el formulario de variación
    const eliminarImagenInForm = useCallback((id, isExisting) => {
        setVariacionForm(prev => {
            const updatedImages = prev.imagenesDraft.filter(img => {
                const isCurrent = isExisting ? img.idImagenVariacion === id : img.id === id;
                return !isCurrent;
            });

            const imgToRemove = prev.imagenesDraft.find(img => (isExisting && img.idImagenVariacion === id) || (!isExisting && img.id === id));
            const wasPrincipal = imgToRemove?.esPrincipal;

            if (wasPrincipal && updatedImages.length > 0) {
                updatedImages[0].esPrincipal = true;
            }

            const newImagesAEliminar = isExisting ? [...(prev.imagenesAEliminar || []), id] : prev.imagenesAEliminar;

            return {
                ...prev,
                imagenesDraft: updatedImages,
                imagenesAEliminar: newImagesAEliminar,
            };
        });
    }, []);


    const guardarOActualizarVariacion = useCallback(() => {
        const { colorActual, tallasSeleccionadas, stockPorTalla, imagenesDraft, idVariacionAEditar } = variacionForm;

        if (!colorActual) { finalShowToast('Seleccione un color', 'warning'); return; }
        if (!tallasSeleccionadas.length) { finalShowToast('Seleccione al menos una talla', 'warning'); return; }

        for (const talla of tallasSeleccionadas) {
            const stock = stockPorTalla[talla];
            if (stock === '' || stock === undefined || Number(stock) < 0 || isNaN(Number(stock))) {
                finalShowToast(`Stock inválido para talla ${talla}`, 'warning');
                return;
            }
        }

        const colorObj = coloresActivos.find(c => c.nombre === colorActual);
        const tallas = tallasSeleccionadas.map(t => ({
            talla: t,
            stock: Number(stockPorTalla[t] ?? 0)
        }));

        const existingImagesFinal = imagenesDraft.filter(i => i.isExisting);
        const newImagesFinal = imagenesDraft.filter(i => i.isNew);

        if (idVariacionAEditar) {
            setVariaciones(prev => prev.map(v => v.idColorBase === idVariacionAEditar ? {
                ...v,
                color: colorActual,
                colorHex: colorObj ? colorObj.valor : v.colorHex,
                tallas,
                imagenesExistentes: existingImagesFinal,
                imagenesNuevas: newImagesFinal,
                imagenesAEliminar: variacionForm.imagenesAEliminar
            } : v));
            finalShowToast('Variación actualizada', 'success');
        } else {
            setVariaciones(prev => [...prev, {
                idColorBase: Date.now(),
                color: colorActual,
                colorHex: colorObj ? colorObj.valor : '#000000',
                tallas,
                imagenesExistentes: existingImagesFinal,
                imagenesNuevas: newImagesFinal,
                imagenesAEliminar: [],
                isNewVariation: true // Marcar como nueva variación
            }]);
            finalShowToast('Variación agregada', 'success');
        }

        resetVariacionForm();
        setMostrarFormularioVariacion(false);
    }, [variacionForm, setVariaciones, finalShowToast, resetVariacionForm]);

    // MANEJO DE TALLAS PERSONALIZADAS
    const agregarTallaPersonalizada = useCallback(() => {
        const value = nuevaTallaPersonalizada.trim();
        if (!value) { finalShowToast('Ingrese una talla válida', 'warning'); return; }
        if (tallasPersonalizadas.includes(value)) { finalShowToast('Esa talla ya existe', 'warning'); return; }
        setTallasPersonalizadas(prev => [...prev, value]);
        setNuevaTallaPersonalizada('');
        finalShowToast('Talla personalizada agregada', 'success');
    }, [nuevaTallaPersonalizada, tallasPersonalizadas, finalShowToast]);

    const eliminarTallaPersonalizada = useCallback((talla) => {
        setTallasPersonalizadas(prev => prev.filter(x => x !== talla));
    }, []);

    // OTRAS FUNCIONES RELEVANTES
    const eliminarVariacion = useCallback((idColorBase) => {
        setVariaciones(prev => prev.filter(v => v.idColorBase !== idColorBase));
        if (variacionForm.idVariacionAEditar === idColorBase) {
            resetVariacionForm();
            setMostrarFormularioVariacion(false);
        }
        finalShowToast('Variación eliminada', 'info');
    }, [variacionForm, finalShowToast, resetVariacionForm]);

    const validarBasicos = useCallback(() => {
        const { nombreProducto, categoriaSeleccionada, precioProducto } = formData;
        if (!nombreProducto.trim()) { finalShowToast('El nombre es obligatorio', 'error'); return false; }
        if (!categoriaSeleccionada) { finalShowToast('Seleccione una categoría', 'error'); return false; }
        if (!precioProducto || Number(precioProducto) <= 0) { finalShowToast('Precio inválido', 'error'); return false; }
        return true;
    }, [formData, finalShowToast]);

    const validarVariaciones = useCallback(() => {
        if (mostrarFormularioVariacion) {
            finalShowToast('Termina de editar el color actual antes de guardar', 'warning');
            return false;
        }

        if (variaciones.length === 0) {
            finalShowToast('Agrega al menos un color', 'error');
            return false;
        }

        for (let i = 0; i < variaciones.length; i++) {
            const v = variaciones[i];
            if (!v.color) { finalShowToast(`Variación ${i + 1} sin color`, 'error'); return false; }
            if (!Array.isArray(v.tallas) || v.tallas.length === 0) { finalShowToast(`Variación ${i + 1} sin tallas`, 'error'); return false; }

            for (const t of v.tallas) {
                const s = Number(t.stock);
                if (!t.talla || Number.isNaN(s) || s < 0) {
                    finalShowToast(`Stock inválido en ${v.color} / ${t.talla}`, 'error');
                    return false;
                }
            }

            const totalImgs = v.imagenesExistentes.length + v.imagenesNuevas.length;
            if (totalImgs > 0) {
                const hayPrincipal =
                    v.imagenesExistentes?.some(img => img.esPrincipal) ||
                    v.imagenesNuevas?.some(img => img.esPrincipal);
                if (!hayPrincipal) {
                    finalShowToast(`Marca una imagen principal para ${v.color}`, 'error');
                    return false;
                }
            }
        }
        return true;
    }, [mostrarFormularioVariacion, variaciones, finalShowToast]);

    const guardarCambios = useCallback(async () => {
        if (!validarBasicos() || !validarVariaciones()) return;
        setMensaje('Guardando...');

        try {
            const payload = {
                nuevoTitulo: formData.nombreProducto.trim(),
                nuevaDescripcion: formData.descripcionActual || '',
                nuevoPrecio: Number(formData.precioProducto),
                nuevaCategoria: Number(formData.categoriaSeleccionada),
                // Obtener el código del tipo de talla seleccionado
                tipoTalla: (() => {
                    const tipoTallaObj = tiposTallas.find(t => t.idTipoTalla === tipoTallaSeleccionado);
                    return tipoTallaObj ? tipoTallaObj.codigo : null;
                })(),
                masVendido: product.masVendido ?? 'no',
                precioAnterior: formData.precioAnteriorProducto !== '' ? Number(formData.precioAnteriorProducto) : '',
                costo: formData.costoProducto !== '' ? Number(formData.costoProducto) : null,
                tallasPersonalizadas: JSON.stringify(tallasPersonalizadas)
            };

            const r1 = await fetch(`${baseURL}/productoTextPut.php?idProducto=${product.idProducto}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const j1 = await r1.json();
            if (!r1.ok || j1?.error) throw new Error(j1?.error || `Error actualizando producto (HTTP ${r1.status})`);

            // --- INICIO DE LA SECCIÓN CRÍTICA DE IMAGENES ---
            const formData2 = new FormData();
            formData2.append('idProducto', product.idProducto);

            // ... dentro de la función guardarCambios
            const accionesPayload = variaciones.map((v, vIdx) => {
                // Usar la marca explícita o distinguir por ID temporal
                const isNewVariation = v.isNewVariation || (v.idColorBase && v.idColorBase > 1000000000000);
                const accion = {
                    action: isNewVariation ? 'create_color' : 'upsert_color',
                    idColorBase: isNewVariation ? null : v.idColorBase,
                    color: v.color,
                    colorHex: v.colorHex,
                    tallas: (v.tallas || []).map(t => ({
                        idItem: t.idItem ?? null,
                        talla: t.talla,
                        stock: Number(t.stock || 0)
                    })),
                    deleteImageIds: v.imagenesAEliminar || []
                };

                const newFilesData = [];
                (v.imagenesNuevas || []).forEach((img, i) => {
                    const fileKey = `file_color_${vIdx}_${i}`;

                    formData2.append(fileKey, img.file);
                    formData2.append(`${fileKey}_alt`, img.alt || '');
                    formData2.append(`${fileKey}_titulo`, img.titulo || '');

                    newFilesData.push({
                        fileKey: fileKey,
                        orden: i + 1,
                        esPrincipal: img.esPrincipal
                    });
                });

                if (newFilesData.length > 0) {
                    accion.newFiles = newFilesData;
                }

                // ... el resto de la lógica para setPrincipal

                return accion; // Retorna la variable declarada
            });
            // ... el resto de la función guardarCambios

            formData2.append('acciones', JSON.stringify(accionesPayload));
            // --- FIN DE LA SECCIÓN CRÍTICA DE IMAGENES ---

            const r2 = await fetch(`${baseURL}/productoVariacionesPost.php`, {
                method: 'POST',
                body: formData2
            });
            const j2 = await r2.json();
            if (!r2.ok || j2?.error) throw new Error(j2?.error || `Error guardando variaciones (HTTP ${r2.status})`);

            finalShowToast(j2?.mensaje || 'Producto actualizado', 'success');
            setMensaje('');
            onSaved?.();
            onClose?.();
        } catch (error) {
            console.error(error);
            setMensaje('');
            finalShowToast(error.message || 'Error al guardar cambios', 'error');
        }
    }, [validarBasicos, validarVariaciones, formData, product, tallasPersonalizadas, variaciones, finalShowToast, onSaved, onClose]);

    return (
        <div className='product-modal-backdrop'>
            <div className='product-modal-container' style={{ maxWidth: 900 }}>
                <Toast toasts={toasts} />
                <div className='product-modal-header'>
                    <div className='product-modal-title'>
                        <FontAwesomeIcon icon={faBox} size="lg" />
                        Editar Producto
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className='btn-product-secondary' onClick={() => recargarVariaciones()}>
                            <FontAwesomeIcon icon={faSync} /> Recargar
                        </button>
                        <button className='product-close-button' onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} size="lg" />
                        </button>
                    </div>
                </div>

                <div className='product-modal-body'>
                    {/* Información Básica */}
                    <div className="product-section-card mb-24">
                        <div className="product-section-header">
                            <div className="product-section-title">
                                <FontAwesomeIcon icon={faBox} /> Información Básica
                            </div>
                        </div>
                        <div className="product-section-content">
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
                                <div className='product-form-group'>
                                    <label className='product-form-label'>
                                        <FontAwesomeIcon icon={faTag} size="sm" /> Nombre *
                                    </label>
                                    <input
                                        className='product-form-input'
                                        value={formData.nombreProducto}
                                        onChange={e => setFormData(prev => ({ ...prev, nombreProducto: e.target.value }))}
                                    />
                                </div>
                                <div className='product-form-group'>
                                    <label className='product-form-label'>
                                        <FontAwesomeIcon icon={faFolder} size="sm" /> Categoría *
                                    </label>
                                    <select
                                        className='product-form-input'
                                        value={formData.categoriaSeleccionada}
                                        onChange={e => setFormData(prev => ({ ...prev, categoriaSeleccionada: e.target.value }))}
                                    >
                                        <option value="">Seleccionar</option>
                                        {categorias.map(c => (
                                            <option key={c.idCategoria} value={c.idCategoria}>
                                                {c.categoria}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className='product-form-group'>
                                    <label className='product-form-label'>
                                        <FontAwesomeIcon icon={faRulerHorizontal} size="sm" /> Tipo de Tallas *
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <select
                                            className='product-form-input'
                                            value={tipoTallaSeleccionado}
                                            onChange={(e) => setTipoTallaSeleccionado(e.target.value)}
                                            style={{ flex: 1, minWidth: 0 }}
                                        >
                                            <option value="">Selecciona un grupo de tallas...</option>
                                            {tiposTallas.map(tipo => (
                                                <option key={tipo.idTipoTalla} value={tipo.idTipoTalla}>
                                                    {tipo.nombre} ({tipo.cantidadTallas || 0} tallas)
                                                </option>
                                            ))}
                                        </select>
                                        {!tipoTallaSeleccionado && (
                                            <small style={{ color: '#f59e0b' }}>
                                                Selecciona primero un grupo para ver las tallas disponibles
                                            </small>
                                        )}
                                        <button
                                            type="button"
                                            onClick={abrirTallaManager}
                                            className="btn-product-icon"
                                            title={`Administrar tallas (${tallasDisponibles.length} disponibles)`}
                                            style={{
                                                padding: '6px',
                                                width: '32px',
                                                height: '32px',
                                                fontSize: '12px',
                                                borderRadius: '6px',
                                                border: '1px solid #d1d5db',
                                                background: '#f9fafb',
                                                color: '#6b7280',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faCog} size="sm" />
                                        </button>
                                    </div>
                                </div>

                                
                                <div className='product-form-group'>
                                    <label className='product-form-label'>
                                        <FontAwesomeIcon icon={faTshirt} size="sm" />  Descripción (opcional)
                                    </label>
                                    <textarea
                                        className='product-form-input'
                                        value={formData.descripcionActual}
                                        onChange={e => setFormData(prev => ({ ...prev, descripcionActual: e.target.value }))}
                                        maxLength={300}  
                                        rows={4}          
                                    />
                                </div>
                            </div>
                            {formData.tipoTallaSeleccionado === 'personalizado' && (
                                <div style={{ marginTop: 20, padding: 16, background: 'var(--gris)', borderRadius: 8, border: '1px solid var(--color2)' }}>
                                    <h4 style={{ margin: '0 0 12px 0', color: 'var(--color2)' }}>
                                        <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 8 }} />
                                        Configurar Tallas Personalizadas
                                    </h4>
                                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                        <input
                                            className='product-form-input'
                                            style={{ flex: 1 }}
                                            value={nuevaTallaPersonalizada}
                                            onChange={e => setNuevaTallaPersonalizada(e.target.value)}
                                            placeholder="Ej: 42, Medium, 90cm..."
                                            onKeyDown={(e) => e.key === 'Enter' && agregarTallaPersonalizada()}
                                        />
                                        <button type="button" onClick={agregarTallaPersonalizada} className='btn-product-primary' style={{ padding: '10px 16px' }}>
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    </div>
                                    {tallasPersonalizadas.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {tallasPersonalizadas.map((t, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--color2)', color: 'var(--blanco)', padding: '4px 8px', borderRadius: 6, fontSize: 12 }}>
                                                    {t}
                                                    <button onClick={() => eliminarTallaPersonalizada(t)} style={{ background: 'none', border: 'none', color: 'var(--blanco)', cursor: 'pointer' }}>
                                                        <FontAwesomeIcon icon={faTimes} size="xs" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Variaciones por Color */}
                    <div className="product-section-card">
                        <div className="product-section-header">
                            <div className="product-section-title">
                                <FontAwesomeIcon icon={faRuler} /> Variaciones por Color
                            </div>
                            <div className="product-section-badge">{variaciones.length} colores</div>
                        </div>
                        <div className="product-section-content">
                            {/* Desktop-only table */}
                            <div className="global-table-container desktop-only mb-24">
                                <table className="global-table">
                                    <thead>
                                        <tr>
                                            <th>Color</th>
                                            <th>Tallas (stock)</th>
                                            <th>Imágenes</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {variaciones.map((v) => (
                                            <tr key={v.idColorBase}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div style={{ width: 20, height: 20, borderRadius: 4, background: v.colorHex, border: '1px solid #ccc' }} />
                                                        {v.color}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                        {(v.tallas || []).map((t, tIdx) => (
                                                            <span key={tIdx} className="global-indicator success">{t.talla} · {t.stock}</span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                                                        <span>{v.imagenesExistentes.length + v.imagenesNuevas.length}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="global-table-actions">
                                                        <button
                                                            type="button"
                                                            className='global-btn-edit'
                                                            onClick={() => iniciarEdicionVariacion(v)}
                                                            title="Editar este color"
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className='global-btn-delete'
                                                            onClick={() => eliminarVariacion(v.idColorBase)}
                                                            title="Eliminar color"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* Mobile-only cards */}
                            <div className="card-list mobile-only mb-24">
                                {variaciones.map((v) => (
                                    <div key={v.idColorBase} className="product-card">
                                        <div className="card-header">
                                            <div className="card-info">
                                                <div className="card-title-container">
                                                    <div className="color-circle" style={{ background: v.colorHex }}></div>
                                                    <div className="card-title">{v.color}</div>
                                                </div>
                                                <div className="card-category">
                                                    {v.imagenesExistentes.length + v.imagenesNuevas.length} imágenes
                                                </div>
                                                <div className="card-price">
                                                    <FontAwesomeIcon icon={faTshirt} size="sm" /> {v.tallas.length} tallas
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card-footer">
                                            <div className="card-stock">
                                                <span className="stock-label">Stock Total:</span>
                                                <span className="stock-value">{v.tallas.reduce((sum, t) => sum + parseInt(t.stock, 10), 0)}</span>
                                            </div>
                                            <div className="card-actions">
                                                <button
                                                    type='button'
                                                    className='global-btn-edit'
                                                    onClick={() => iniciarEdicionVariacion(v)}
                                                    title="Editar este color"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button
                                                    type='button'
                                                    className='global-btn-delete'
                                                    onClick={() => eliminarVariacion(v.idColorBase)}
                                                    title="Eliminar color"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Botón para agregar si el formulario no está visible */}
                            {!mostrarFormularioVariacion && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                                    <button
                                        type='button'
                                        onClick={() => setMostrarFormularioVariacion(true)}
                                        className='btn-product-primary'
                                        style={{ padding: '8px 16px', fontSize: 14 }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} /> Agregar otra variación
                                    </button>
                                </div>
                            )}
                            {/* Formulario de agregar/editar variación */}
                            {mostrarFormularioVariacion && (
                                <div className="product-section-card mb-24" style={{ border: variacionForm.idVariacionAEditar ? '2px solid var(--color2)' : '1px solid var(--gris2)' }}>
                                    <div className="product-section-header">
                                        <div className="product-section-title">
                                            <FontAwesomeIcon icon={faPalette} />
                                            {variacionForm.idVariacionAEditar ? `Editando: ${variacionForm.colorActual}` : 'Agregar Nuevo Color'}
                                        </div>
                                        <div className="product-section-badge">Tallas Gestionadas ({tallasComoStrings.length} disponibles)</div>
                                    </div>
                                    <div className="product-section-content">
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
                                            <div className='product-form-group'>
                                                <label className='product-form-label'>
                                                    <FontAwesomeIcon icon={faPalette} size="sm" /> Color *
                                                </label>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <select
                                                        className='product-form-input'
                                                        value={variacionForm.colorActual}
                                                        onChange={(e) => setVariacionForm(prev => ({ ...prev, colorActual: e.target.value }))}
                                                        disabled={variacionForm.idVariacionAEditar !== null}
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            opacity: variacionForm.idVariacionAEditar ? 0.7 : 1,
                                                            cursor: variacionForm.idVariacionAEditar ? 'not-allowed' : 'pointer'
                                                        }}
                                                    >
                                                        {!variacionForm.colorActual && <option value="">Seleccionar color</option>}
                                                        {coloresActivos.map(c => {
                                                            const enUso = usedColors.has(norm(c.nombre)) && variacionForm.colorActual !== c.nombre;
                                                            return (<option key={c.idColor || c.nombre} value={c.nombre} disabled={enUso}>{c.nombre}</option>);
                                                        })}
                                                    </select>
                                                    {variacionForm.colorActual && (
                                                        <div style={{ marginTop: 8, width: 30, height: 20, borderRadius: 4, backgroundColor: coloresActivos.find(c => c.nombre === variacionForm.colorActual)?.valor, border: '1px solid #ccc' }} />
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowColorManager(true)}
                                                        className="btn-product-icon"
                                                        title={`Administrar colores (${coloresActivos.length} disponibles)`}
                                                        style={{
                                                            padding: '6px',
                                                            width: '32px',
                                                            height: '32px',
                                                            fontSize: '12px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #d1d5db',
                                                            background: '#f9fafb',
                                                            color: '#6b7280',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faCog} size="sm" />
                                                    </button>
                                                </div>
                                                {coloresActivos.length === 0 && (
                                                    <div style={{ marginTop: 8, padding: 8, background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 4, fontSize: 12 }}>
                                                        No hay colores disponibles. Haz clic en el ícono de configuración para agregar colores.
                                                    </div>
                                                )}
                                                {variacionForm.idVariacionAEditar && (
                                                    <div style={{ marginTop: 8, padding: 8, background: '#e0e7ff', border: '1px solid #6366f1', borderRadius: 4, fontSize: 12 }}>
                                                        ℹ️ El color no se puede cambiar durante la edición de una variación existente.
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="product-form-group" style={{ gridColumn: '1 / -1' }}>
                                            <label className="product-form-label">
                                                <FontAwesomeIcon icon={faTshirt} size="sm" />
                                                {variacionForm.idVariacionAEditar ? `Modificar tallas para ${variacionForm.colorActual}` : `Agregar tallas al color ${variacionForm.colorActual || '(selecciona)'}`} *
                                            </label>
                                            {!tipoTallaSeleccionado ? (
                                                <div className="empty-description" style={{ background: '#fef3c7', padding: '12px', borderRadius: '6px', border: '1px solid #f59e0b' }}>
                                                    <FontAwesomeIcon icon={faRulerHorizontal} style={{ marginRight: '8px', color: '#f59e0b' }} />
                                                    Primero selecciona un <strong>Tipo de Tallas</strong> arriba para ver las tallas disponibles de ese grupo.
                                                </div>
                                            ) : tallasDisponibles.length === 0 ? (
                                                <div className="empty-description" style={{ background: '#fef2f2', padding: '12px', borderRadius: '6px', border: '1px solid #ef4444' }}>
                                                    <FontAwesomeIcon icon={faTshirt} style={{ marginRight: '8px', color: '#ef4444' }} />
                                                    No hay tallas configuradas en este grupo. Ve al <strong>Administrar Tallas</strong> para agregar tallas.
                                                </div>
                                            ) : (
                                                <div className="tallas-grid">
                                                    {tallasDisponibles.map((t) => {
                                                        const checked = variacionForm.tallasSeleccionadas.includes(t);
                                                        return (
                                                            <button
                                                                key={t}
                                                                type="button"
                                                                className={`talla-chip ${checked ? 'selected' : ''}`}
                                                                onClick={() => {
                                                                    const newTallas = checked ? variacionForm.tallasSeleccionadas.filter(x => x !== t) : [...variacionForm.tallasSeleccionadas, t];
                                                                    const newStock = { ...variacionForm.stockPorTalla };
                                                                    if (checked) delete newStock[t];
                                                                    else if (newStock[t] === undefined) newStock[t] = '';
                                                                    setVariacionForm(prev => ({ ...prev, tallasSeleccionadas: newTallas, stockPorTalla: newStock }));
                                                                }}>
                                                                {t}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {tipoTallaSeleccionado && (
                                                <div className="tallas-counter">
                                                    {variacionForm.tallasSeleccionadas.length} de {tallasDisponibles.length} talla(s) seleccionada(s)
                                                    {tiposTallas.find(t => t.idTipoTalla == tipoTallaSeleccionado) && (
                                                        <span style={{ color: '#6b7280', marginLeft: '8px' }}>
                                                            del grupo "{tiposTallas.find(t => t.idTipoTalla == tipoTallaSeleccionado)?.nombre}"
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {variacionForm.tallasSeleccionadas.length > 0 && (
                                            <div className="product-form-group" style={{ gridColumn: '1 / -1' }}>
                                                <div className="product-form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                    <FontAwesomeIcon icon={faWarehouse} size="sm" /> Stock por talla *
                                                </div>
                                                <div className="tallas-stock-grid">
                                                    {variacionForm.tallasSeleccionadas.map(t => (
                                                        <div key={t} className="talla-stock-card">
                                                            <span className="talla-pill">{t}</span>
                                                            <div className="talla-input-row">
                                                                <input type="number" min="0" inputMode="numeric" className="product-form-input" value={variacionForm.stockPorTalla[t] ?? ''} onChange={(e) => setVariacionForm(prev => ({ ...prev, stockPorTalla: { ...prev.stockPorTalla, [t]: e.target.value } }))} placeholder="0" />
                                                                <span className="talla-sufijo">unid.</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="product-form-group" style={{ gridColumn: '1 / -1' }}>
                                            <label className='product-form-label'>
                                                <FontAwesomeIcon icon={faImages} size="sm" /> Imágenes de la variación (opcional)
                                            </label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                className="product-form-input"
                                                onChange={handleFileChangeInForm}
                                            />
                                            {variacionForm.imagenesDraft.length > 0 && (
                                                <div className="product-image-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 12 }}>
                                                    {variacionForm.imagenesDraft.map((img, idx) => {
                                                        const isExisting = !img.isNew;
                                                        const isPrincipal = !!img.esPrincipal;

                                                        return (
                                                            <div
                                                                key={isExisting ? `ex-${img.idImagenVariacion}` : `new-${idx}-${img.file?.name}`}
                                                                className="image-thumb"
                                                                style={{ border: `2px solid ${isPrincipal ? 'var(--color2)' : 'var(--gris2)'}`, borderRadius: 8, padding: 8 }}
                                                            >
                                                                <div className="image-frame" style={{ position: 'relative', marginBottom: 8 }}>
                                                                    <button
                                                                        type="button"
                                                                        className="global-btn-delete as-icon"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            eliminarImagenInForm(isExisting ? img.idImagenVariacion : img.file, isExisting);
                                                                        }}
                                                                        aria-label="Eliminar imagen"
                                                                        title="Eliminar imagen"
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </button>
                                                                    <img
                                                                        src={isExisting ? img.imagen : img.preview}
                                                                        alt={img.alt || `img-${idx}`}
                                                                        style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 6 }}
                                                                    />
                                                                    {isPrincipal && (
                                                                        <span
                                                                            style={{
                                                                                position: 'absolute',
                                                                                top: 6,
                                                                                left: 6,
                                                                                background: 'var(--color2)',
                                                                                color: 'var(--blanco)',
                                                                                fontSize: 10,
                                                                                padding: '2px 6px',
                                                                                borderRadius: 6
                                                                            }}
                                                                        >
                                                                            Principal
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {!isPrincipal && (
                                                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                                                        <button
                                                                            type="button"
                                                                            className="btn-product-primary"
                                                                            onClick={() => setPrincipalImagenInForm(isExisting ? img.idImagenVariacion : img.file, isExisting)}
                                                                        >
                                                                            Marcar principal
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        <div className="action-buttons-container">
                                            <button
                                                type="button"
                                                onClick={guardarOActualizarVariacion}
                                                className="btn-product-primary flex-center"
                                                disabled={!variacionForm.colorActual || variacionForm.tallasSeleccionadas.length === 0}
                                            >
                                                <FontAwesomeIcon icon={faPlus} size="sm" />{" "}
                                                {variacionForm.idVariacionAEditar
                                                    ? `Guardar Cambios en ${variacionForm.colorActual}`
                                                    : `Agregar Color ${variacionForm.colorActual || ""}`}
                                            </button>

                                            {variaciones.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        resetVariacionForm();
                                                        setMostrarFormularioVariacion(false);
                                                    }}
                                                    className="global-btn-delete flex-center"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} /> Cancelar
                                                </button>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Precios */}
                    <div className="product-section-card mb-24" style={{ marginTop: 24 }}>
                        <div className="product-section-header">
                            <div className="product-section-title">
                                <FontAwesomeIcon icon={faDollarSign} /> Precios del Producto
                            </div>
                            <div className="product-section-badge">Aplica a todas las variaciones</div>
                        </div>
                        <div className="product-section-content">
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                <div className='product-form-group'>
                                    <label className='product-form-label'>💰 Costo *</label>
                                    <input
                                        type='number'
                                        className='product-form-input'
                                        value={formData.costoProducto}
                                        onChange={e => setFormData(prev => ({ ...prev, costoProducto: e.target.value }))}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                <div className='product-form-group'>
                                    <label className='product-form-label'>
                                        <FontAwesomeIcon icon={faDollarSign} size="sm" /> Precio *
                                    </label>
                                    <input
                                        type='number'
                                        className='product-form-input'
                                        value={formData.precioProducto}
                                        onChange={e => setFormData(prev => ({ ...prev, precioProducto: e.target.value }))}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                <div className='product-form-group'>
                                    <label className='product-form-label'>
                                        <FontAwesomeIcon icon={faDollarSign} size="sm" /> Precio Anterior
                                    </label>
                                    <input
                                        type='number'
                                        className='product-form-input'
                                        value={formData.precioAnteriorProducto}
                                        onChange={e => setFormData(prev => ({ ...prev, precioAnteriorProducto: e.target.value }))}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                            </div>
                            {formData.precioProducto && formData.costoProducto && Number(formData.precioProducto) > 0 && Number(formData.costoProducto) > 0 && (
                                <div style={{ marginTop: 16, padding: 12, background: Number(formData.precioProducto) > Number(formData.costoProducto) ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.1)', borderRadius: 8, border: `1px solid ${Number(formData.precioProducto) > Number(formData.costoProducto) ? '#10b981' : '#ef4444'}` }}>
                                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Análisis de Rentabilidad:</div>
                                    <div style={{ color: Number(formData.precioProducto) > Number(formData.costoProducto) ? '#16a34a' : '#dc2626', fontSize: 14 }}>
                                        {Number(formData.precioProducto) > Number(formData.costoProducto) ? (
                                            <>
                                                <strong>Margen: {resumen.margen}%</strong><br />
                                                Ganancia por unidad: {moneda}{(Number(formData.precioProducto) - Number(formData.costoProducto)).toFixed(2)}
                                            </>
                                        ) : (
                                            <strong>⚠️ El precio debe ser mayor al costo</strong>
                                        )}
                                    </div>
                                </div>
                            )}
                            {variaciones.length > 0 && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 16, padding: 16, background: 'var(--gris)', borderRadius: 8 }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontWeight: 'bold', color: 'var(--color2)' }}>{resumen.totalVariaciones}</div>
                                        <div style={{ fontSize: 12, color: 'var(--text-color2)' }}>Colores</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontWeight: 'bold', color: 'var(--color2)' }}>{moneda}{formData.precioProducto ? Number(formData.precioProducto).toFixed(2) : '0.00'}</div>
                                        <div style={{ fontSize: 12, color: 'var(--text-color2)' }}>Precio</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontWeight: 'bold', color: '#f59e0b' }}>{moneda}{formData.costoProducto ? Number(formData.costoProducto).toFixed(2) : '0.00'}</div>
                                        <div style={{ fontSize: 12, color: 'var(--text-color2)' }}>Costo</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontWeight: 'bold', color: 'var(--color2)' }}>{resumen.stockTotal}</div>
                                        <div style={{ fontSize: 12, color: 'var(--text-color2)' }}>Stock Total</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className='product-submit-section'>
                    <button type='button' onClick={onClose} className='btn-product-secondary'>Cancelar</button>
                    {mensaje ? (
                        <button type='button' className='btn-product-primary' disabled style={{ opacity: .6 }}>
                            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 8 }} />
                            {mensaje}
                        </button>
                    ) : (
                        <button type='button' onClick={guardarCambios} className='btn-product-primary flex-center' style={{ gap: 8 }} disabled={mostrarFormularioVariacion}>
                            <FontAwesomeIcon icon={faCheck} size="sm" />
                            Guardar Cambios
                            {mostrarFormularioVariacion && (<span style={{ marginLeft: 8, fontSize: 11, opacity: 0.8 }}>(Termina la edición primero)</span>)}
                        </button>
                    )}
                </div>
            </div>

            {/* Modal del administrador de colores */}
            {showColorManager && (
                <div className='product-modal-backdrop'>
                    <div className='product-modal-container' style={{ maxWidth: 1000 }}>
                        <div className='product-modal-header'>
                            <div className='product-modal-title'>
                                <FontAwesomeIcon icon={faPalette} size="lg" /> Administrador de Colores
                            </div>
                            <button className='product-close-button' onClick={() => setShowColorManager(false)}>
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                            </button>
                        </div>

                        <div className='product-modal-body'>
                            <ColorManager
                                onColorsUpdated={(nuevosColores) => {
                                    cargarColores();
                                }}
                            />
                        </div>

                        <div className='product-submit-section'>
                            <button
                                type='button'
                                onClick={() => setShowColorManager(false)}
                                className='btn-product-primary'
                            >
                                <FontAwesomeIcon icon={faCheck} size="sm" /> Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal del administrador de tallas */}
            {showTallaManager && (
                <div className='product-modal-backdrop'>
                    <div className='product-modal-container' style={{ maxWidth: 1000 }}>
                        <div className='product-modal-header'>
                            <div className='product-modal-title'>
                                <FontAwesomeIcon icon={faRulerHorizontal} size="lg" /> Administrador de Tallas
                            </div>
                            <button className='product-close-button' onClick={() => setShowTallaManager(false)}>
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                            </button>
                        </div>

                        <div className='product-modal-body'>
                            <TallaManager
                                onTallasUpdated={(nuevasTallas) => {
                                    cargarTallas();
                                }}
                            />
                        </div>

                        <div className='product-submit-section'>
                            <button
                                type='button'
                                onClick={() => setShowTallaManager(false)}
                                className='btn-product-primary'
                            >
                                <FontAwesomeIcon icon={faCheck} size="sm" /> Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}